import type { NetworkData } from '@dynamic-labs-sdk/client';
import { FeeEstimationFailedError } from '@dynamic-labs-sdk/client';
import { Connection, LAMPORTS_PER_SOL, type Transaction, type VersionedTransaction } from '@solana/web3.js';

import type { SolanaTransactionFeeData } from '../../transactionSimulation.types';
import { isVersionedTransaction } from '../isVersionedTransaction';

type CalculateSolanaTransactionFeeParams = {
  nativeTokenPriceUsd?: number;
  networkData: NetworkData;
  transaction: Transaction | VersionedTransaction;
};

const MAX_RETRIES = 5;
const RETRY_DELAY_MS = 500;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

/**
 * Calculates the estimated fee for a Solana transaction.
 *
 * This function fetches the transaction fee by compiling the transaction message
 * and querying the network. It handles both legacy Transaction and VersionedTransaction
 * formats and includes retry logic for network requests.
 *
 * @param params.transaction - The Solana transaction to estimate fees for.
 * @param params.networkData - Network configuration for RPC connection.
 * @param params.nativeTokenPriceUsd - Optional USD price of SOL for fee conversion.
 * @returns Fee data in lamports and human-readable SOL format.
 * @throws {FeeEstimationFailedError} If fee retrieval fails after retries.
 */
export const calculateSolanaTransactionFee = async ({
  transaction,
  networkData,
  nativeTokenPriceUsd,
}: CalculateSolanaTransactionFeeParams): Promise<SolanaTransactionFeeData> => {
  try {
    const connection = new Connection(networkData.rpcUrls.http[0]);

    // Ensure transaction has a recent blockhash
    if (isVersionedTransaction(transaction)) {
      if (!transaction.message.recentBlockhash) {
        const { blockhash } = await connection.getLatestBlockhash();
        transaction.message.recentBlockhash = blockhash;
      }
    } else {
      if (!transaction.recentBlockhash) {
        const { blockhash } = await connection.getLatestBlockhash();
        transaction.recentBlockhash = blockhash;
      }
    }

    // Compile the transaction message
    const message = isVersionedTransaction(transaction)
      ? transaction.message
      : transaction.compileMessage();

    // Get fee with retry logic
    let lastError: Error | undefined;

    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        const feeResponse = await connection.getFeeForMessage(message);

        if (feeResponse.value === null) {
          throw new FeeEstimationFailedError({
            message: 'Fee calculation returned null',
          });
        }

        const nativeAmount = BigInt(feeResponse.value);
        const humanReadableAmount = (Number(nativeAmount) / LAMPORTS_PER_SOL).toString();

        // Calculate USD amount if price data available
        const usdAmount = nativeTokenPriceUsd
          ? (Number(humanReadableAmount) * nativeTokenPriceUsd).toFixed(2)
          : undefined;

        return {
          humanReadableAmount,
          nativeAmount,
          usdAmount,
        };
      } catch (error) {
        lastError = error instanceof Error ? error : undefined;

        if (attempt < MAX_RETRIES - 1) {
          await sleep(RETRY_DELAY_MS * (attempt + 1));
        }
      }
    }

    throw new FeeEstimationFailedError({
      cause: lastError,
      message: 'Failed to get fee after retries',
    });
  } catch (error) {
    throw new FeeEstimationFailedError({
      cause: error instanceof Error ? error : undefined,
      message: 'Failed to estimate Solana transaction fee',
    });
  }
};
